/**
 * German strings merged under `de` when Supabase `translations.de` is missing
 * or stale (local cache seed). DB values win over these defaults when present.
 *
 * Keeps the owner portal usable in Deutsch without requiring every key row.
 */
export const OWNER_PORTAL_DE_FALLBACK: Record<string, string> = {
  // Owner demo CTA. These ship in the client as well as public.translations so
  // the floating control never flashes English while a new migration or the
  // translation cache is still rolling out.
  "owner.demo.bubble": "Demo buchen",
  "owner.demo.bubbleSubline": "30 Min · Einführung",
  "owner.demo.title": "Demo-Gespräch buchen",
  "owner.demo.meta": "30 Minuten · Videocall · kostenlos",
  "owner.demo.close": "Schließen",
  "owner.demo.previousMonth": "Vorheriger Monat",
  "owner.demo.nextMonth": "Nächster Monat",
  "owner.demo.chooseDay": "Wählen Sie einen Tag, um freie Zeiten zu sehen.",
  "owner.demo.timezone": "MEZ · Berlin",
  "owner.demo.continue": "Weiter",
  "owner.demo.changeTime": "Zeit ändern",
  "owner.demo.name": "Name",
  "owner.demo.email": "E-Mail",
  "owner.demo.note": "Worum geht es? (optional)",
  "owner.demo.notePlaceholder": "z. B. zweites Objekt aufnehmen",
  "owner.demo.confirm": "Termin bestätigen",
  "owner.demo.done": "Termin gebucht.",
  "owner.demo.inviteSent": "Die Einladung mit Videolink geht an {email}.",
  "owner.demo.liveTimes": "Freie Zeiten live aus dem Kalender",
  "owner.demo.provider": "via Calendly",
  "owner.demo.loading": "Freie Zeiten werden geladen…",
  "owner.demo.noTimes": "In den nächsten 30 Tagen gibt es keine freien Zeiten.",
  "owner.demo.retry": "Erneut versuchen",
  "owner.demo.fallback": "Calendly öffnen",
  "owner.demo.error": "Freie Zeiten konnten nicht geladen werden.",
  "owner.demo.bookingError": "Der Termin konnte nicht gebucht werden.",
  "owner.demo.alreadyBooked":
    "Für dieses Konto ist bereits ein Demo-Termin gebucht.",
  "owner.demo.slotTaken":
    "Diese Zeit wurde gerade gebucht. Bitte wählen Sie eine andere.",
  "owner.demo.required":
    "Bitte geben Sie einen gültigen Namen und eine gültige E-Mail-Adresse ein.",
  "owner.demo.appointment": "Demo am {date}",
  "owner.demo.reschedule": "Verschieben",
  "owner.demo.cancel": "Termin absagen",
  // The owner portal's own sidebar, and the banner an admin sees while acting
  // as an owner. The nav labels have DB rows (20261005140200 / 20261012085012),
  // but this file is what renders when the row is missing or the cache is
  // stale — and these seven fell back to ENGLISH words inside an otherwise
  // German shell. Named in the PRD as needing checking; they did.
  "admin.nav.ownerDocuments": "Dokumente",
  "admin.nav.ownerPayouts": "Auszahlungen",
  "admin.nav.ownerNotifications": "Benachrichtigungen",
  "admin.nav.ownerReferrals": "Freunde werben",
  "admin.nav.settings": "Einstellungen",
  // The impersonation banner is rendered globally, so a German owner account
  // being supported by an admin saw "Acting as owner:" over a German portal.
  "admin.impersonation.actingAs": "Angemeldet als",
  "admin.impersonation.roleOwner": "Eigentümer",
  "admin.impersonation.roleTenant": "Mieter",
  "admin.impersonation.exit": "Beenden",
  "admin.impersonation.exiting": "Wird beendet…",

  // Owners admin (renamed from "Lessors"). Nav tab and add-owner button use
  // "Eigentümer"; other owner-record strings below keep "Vermieter".
  "admin.nav.owners": "Eigentümer",
  "admin.owner.addAction": "Eigentümer hinzufügen",
  "admin.owner.add": "Vermieter hinzufügen",
  "admin.owner.edit": "Vermieter bearbeiten",
  "admin.field.owner": "Vermieter",
  "admin.field.searchOwners": "Vermieter nach Name suchen…",
  "admin.sidebar.contract": "Vertrag",
  "admin.empty.noOwnersTitle": "Noch keine Vermieter",
  "admin.empty.noOwnersDesc":
    "Legen Sie Ihren ersten Vermieter an — einen Eigentümerdatensatz mit Standard-Vertreter und Bankdaten. Vermieter werden über den Assistenten mit Immobilien verknüpft; ihre Vorgaben fließen automatisch in Verträge und Auszahlungen ein.",
  "admin.error.couldNotLoadOwners": "Vermieter konnten nicht geladen werden",
  "admin.revenue.perMonthToOwner": "/ Monat, an den Vermieter",
  "admin.inheritance.fromOwner": "vom Vermieter",

  // Owner property detail (read-only operational view)
  "owner.detail.back": "Zurück zur Analyse",
  "owner.detail.property": "Immobilie",
  "owner.detail.loadErrorTitle": "Immobilie konnte nicht geladen werden",
  "owner.detail.notFoundTitle": "Immobilie nicht verfügbar",
  "owner.detail.notFoundHint":
    "Diese Immobilie wurde möglicherweise entfernt oder ist Ihrem Konto nicht zugewiesen.",
  "owner.detail.kpiOccupiedRooms": "Belegte Zimmer",
  "owner.detail.kpiMonthlyRent": "Monatsmiete",
  "owner.detail.kpiOutstanding": "Offen",
  // One table: room → tenant → move-in → rent status. Not "Zimmerübersicht"
  // any more — it lists the tenants too.
  "owner.detail.roomOverview": "Übersicht",
  "owner.detail.apartmentDocuments": "Wohnungsdokumente",
  "owner.detail.noOwnerDocumentsShort":
    "Noch keine Dokumente für diese Immobilie.",
  "owner.detail.viewAllDocuments": "Alle anzeigen",
  "owner.detail.kpiRooms": "Zimmer",
  "owner.detail.kpiRoomsHint": "Belegt von gesamt",
  "owner.detail.kpiOccupancy": "Belegung",
  "owner.detail.kpiVacant": "frei",
  "owner.detail.kpiTenants": "Aktive Mieter",
  "owner.detail.kpiTenantsHint": "Aktuell wohnhaft",
  "owner.detail.kpiCollected": "Eingenommen (12M)",
  "owner.detail.kpiUpcoming": "Anstehend",
  "owner.detail.tabOverview": "Übersicht",
  "owner.detail.tabRooms": "Zimmer",
  "owner.detail.tabTenants": "Mieter",
  "owner.detail.tabDocuments": "Dokumente",
  "owner.detail.pastTenancies": "Frühere Mietverhältnisse",
  "owner.detail.noRooms": "Noch keine Zimmer in dieser Immobilie.",
  "owner.detail.noTenants": "Noch keine Mieter in dieser Immobilie.",
  "owner.detail.noDocuments":
    "Noch keine Dokumente für diese Immobilie hochgeladen.",
  "owner.detail.colTenant": "Mieter",
  "owner.detail.colRoom": "Zimmer",
  "owner.detail.colFloor": "Etage",
  "owner.detail.colSize": "Größe",
  "owner.detail.colRent": "Miete",
  "owner.detail.colStatus": "Status",
  "owner.detail.colMovein": "Einzug",
  "owner.detail.colMoveout": "Auszug",
  "owner.detail.colContract": "Vertrag",
  "owner.detail.colPayment": "Zahlung",
  "owner.detail.colRentStatus": "Mietstatus",
  "owner.detail.rentPaid": "Bezahlt",
  "owner.detail.rentPending": "Offen",
  "owner.detail.tenant": "Mieter",
  "owner.detail.colDoc": "Dokument",
  "owner.detail.colDocTitle": "Dokument",
  "owner.detail.colType": "Typ",
  "owner.detail.colDate": "Datum",
  "owner.detail.occupied": "Belegt",
  "owner.detail.vacant": "Frei",
  "owner.detail.open": "Öffnen",

  // Uploading — contextual, wherever the thing being documented lives.
  "owner.upload.title": "Dokument hochladen",
  "owner.upload.choose": "Datei auswählen",
  "owner.upload.hint": "Klicken oder Datei hierher ziehen",
  // Upload card — level picker (property/apartment/room/tenant) and the
  // multi-place disclosure.
  "owner.upload.levelHint":
    "Wählen Sie die Ebene, zu der es gehört, und fügen Sie dann die Datei hinzu.",
  "owner.upload.filedAgainst": "Abgelegt bei",
  "owner.upload.pickApartment": "Wohnung auswählen",
  "owner.upload.pickRoom": "Zimmer auswählen",
  "owner.upload.pickTenant": "Mieter auswählen",
  "owner.upload.alsoFile": "Auch an anderen Stellen ablegen",
  "owner.upload.alsoFileHint": "Dieselbe Datei, zusätzlich hier abgelegt.",

  // Merged document feed — search box, level filter chips, result count.
  "owner.docs.searchLabel": "Dokumente durchsuchen",
  "owner.docs.searchPlaceholder": "Dokumente, Mieter, Zimmer durchsuchen…",
  "owner.docs.filterAll": "Alle",
  "owner.docs.filterProperty": "Immobilie",
  "owner.docs.filterApartment": "Wohnung",
  "owner.docs.filterRoom": "Zimmer",
  "owner.docs.filterTenant": "Mieter",
  "owner.docs.documentOne": "Dokument",
  "owner.docs.documentMany": "Dokumente",
  "owner.docs.noMatches": "Keine Dokumente entsprechen dieser Suche.",
  "owner.docs.fromTenant": "Vom Mieter hochgeladen",
  // Now carries admin-filed and system-generated tenant documents too, not
  // just property-level staff uploads, so a German owner meets it far more
  // often than before and it cannot stay untranslated.
  "owner.docs.fromColiviq": "Von coliviq",
  // Lists every level filed under this building — property, apartment, room
  // and tenant. Only the upload box on it is property-scoped.
  "owner.detail.tabOwnerDocuments": "Dokumente in dieser Immobilie",
  "owner.detail.noOwnerDocuments":
    "In dieser Immobilie ist noch nichts hinterlegt.",
  "owner.detail.uploadToProperty":
    "Wird der gesamten Immobilie zugeordnet. Für eine Wohnung, ein Zimmer oder einen Mieter öffnen Sie diese und laden dort hoch.",
  "owner.detail.apartments": "Wohnungen",
  "owner.detail.apartment": "Wohnung",
  "owner.docs.scopeTenant": "Mieter",
  "owner.room.uploadHint": "Wird diesem Zimmer zugeordnet.",

  // Apartment view (owner.apartment.*)
  "owner.apartment.documents": "Wohnungsdokumente",
  "owner.apartment.uploadHint":
    "Wird dieser Wohnung zugeordnet. coliviq sieht es im selben Tab.",
  "owner.apartment.noDocuments":
    "Keine Dokumente zu dieser Wohnung hinterlegt.",
  "owner.apartment.noRooms": "Noch keine Zimmer in dieser Wohnung.",
  "owner.apartment.loadErrorTitle": "Wohnung konnte nicht geladen werden",
  "owner.apartment.notFoundTitle": "Wohnung nicht verfügbar",
  "owner.apartment.notFoundHint":
    "Diese Wohnung wurde möglicherweise entfernt oder liegt in einer Immobilie, die Ihrem Konto nicht zugewiesen ist.",

  // Tenant panel — documents filed against the tenancy.
  "owner.tenantView.filedDocs": "Zu diesem Mieter hinterlegt",
  "owner.tenantView.uploadHint":
    "Wird diesem Mietverhältnis zugeordnet und als Ihr Upload gekennzeichnet.",
  "owner.tenantView.noFiledDocs":
    "Zu diesem Mietverhältnis ist noch nichts hinterlegt.",
  "owner.tenantView.signedContract": "Unterschriebener Vertrag",
  // Badges on the owner tenant profile (20261021150100).
  "owner.tenantView.stepOf": "Schritt {n} von {total}",
  "owner.tenantView.contractStatus.signed": "Unterschrieben",
  "owner.tenantView.contractStatus.approved": "Freigegeben",
  "owner.tenantView.contractStatus.ready_to_sign": "Bereit zur Unterschrift",
  "owner.tenantView.contractStatus.awaiting_approval": "Wartet auf Freigabe",
  "owner.tenantView.contractStatus.draft": "Entwurf",
  "owner.tenantView.contractStatus.envelope_draft": "Entwurf",
  "owner.tenantView.contractStatus.not_ready": "Noch nicht bereit",
  "owner.tenantView.contractStatus.pending": "Ausstehend",
  "owner.tenantView.paymentStatus.paid": "Bezahlt",
  "owner.tenantView.paymentStatus.pending": "Ausstehend",
  "owner.tenantView.paymentStatus.overdue_1": "Überfällig (1. Mahnung)",
  "owner.tenantView.paymentStatus.overdue_2": "Überfällig (2. Mahnung)",
  "owner.tenantView.paymentStatus.overdue_3": "Überfällig (3. Mahnung)",
  "owner.tenantView.paymentStatus.inkasso": "Inkasso",
  "owner.tenantView.docStatus.pending": "Ausstehend",
  "owner.tenantView.docStatus.approved": "Freigegeben",
  "owner.tenantView.docStatus.signed": "Unterschrieben",
  "owner.tenantView.docStatus.rejected": "Abgelehnt",
  "owner.tenantView.docType.id_passport": "Ausweis / Reisepass",
  "owner.tenantView.docType.residence_permit": "Aufenthaltstitel / Visum",
  "owner.tenantView.docType.proof_of_income": "Einkommensnachweis",
  "owner.tenantView.docType.employment_letter": "Arbeitgeberbescheinigung",
  "owner.tenantView.docType.reference": "Referenz",
  "owner.tenantView.docType.contract": "Vertrag",
  "owner.tenantView.docType.move_in_protocol": "Einzugsprotokoll",
  "owner.tenantView.docType.move_out_protocol": "Auszugsprotokoll",
  "owner.tenantView.docType.deposit_statement": "Kautionsabrechnung",
  "owner.tenantView.docType.wohnungsgeberbestaetigung":
    "Wohnungsgeberbestätigung",
  "owner.tenantView.docType.other": "Sonstiges",
  "owner.tenantView.paymentType.rent": "Miete",
  "owner.tenantView.paymentType.deposit": "Kaution",
  "owner.tenantView.paymentType.membership": "Mitgliedschaft",

  // Admin tenant profile — who filed what.
  "admin.tenant.filedDocuments": "Zu diesem Mietverhältnis hinterlegt",
  "admin.tenant.ownerUploaded": "Vom Eigentümer hochgeladen",
  "admin.tenant.coliviqUploaded": "Von coliviq hochgeladen",

  // Room view (owner.room.*) — the read-only page behind a room link.
  "owner.room.currentTenant": "Aktueller Mieter",
  "owner.room.vacant": "Dieses Zimmer ist derzeit frei.",
  "owner.room.documents": "Zimmerdokumente",
  "owner.room.noDocuments": "Keine Dokumente zu diesem Zimmer hinterlegt.",
  "owner.room.noHistory": "Aus diesem Zimmer ist noch niemand ausgezogen.",
  "owner.room.loadErrorTitle": "Zimmer konnte nicht geladen werden",
  "owner.room.notFoundTitle": "Zimmer nicht verfügbar",
  "owner.room.notFoundHint":
    "Dieses Zimmer wurde möglicherweise entfernt oder liegt in einer Immobilie, die Ihrem Konto nicht zugewiesen ist.",
  // Revenue is rent only — deposits are held in trust, not earned.
  "owner.analytics.revenueTrendDesc":
    "Monatlich eingezogene Miete, letzte 12 Monate.",
  "owner.analytics.chartEmpty":
    "In den letzten 12 Monaten wurde noch keine Miete eingezogen.",
  // Due-month analytics (COL-589)
  "owner.analytics.kpiOpen": "Offen",
  "owner.analytics.kpiOpenNone": "Nichts offen",
  "owner.analytics.kpiOpenTenant": "Mieter",
  "owner.analytics.kpiOpenTenants": "Mieter",
  "owner.analytics.kpiOpenOverdue": "davon überfällig {x}",
  "owner.analytics.legendCollected": "Eingezogen",
  "owner.analytics.legendOpen": "Offen",
  "owner.analytics.chartEmptyDue":
    "In den letzten 12 Monaten war noch keine Miete fällig.",
  "owner.analytics.revenueTrendDescDue":
    "Miete je Fälligkeitsmonat, eingezogen und offen, letzte 12 Monate.",
  "owner.analytics.tableDueMonth": "Soll / Monat",
  "owner.analytics.tableCollected": "Eingezogen",
  "owner.analytics.tableOpen": "Offen",
  "owner.analytics.csvDue": "Miete (Soll)",
  "owner.analytics.csvCollected": "Eingezogen",
  "owner.analytics.csvOpen": "Offen",
  "admin.nav.pendingSubmissions": "Immobilien warten auf Prüfung",
  "owner.analytics.propertyPerformanceDescV2":
    "Umsatz und Belegung über Ihr Portfolio — wählen Sie eine Immobilie, um Zimmer, Mieter, Dokumente und Auszüge zu sehen.",

  // Account & profile (staff variant of the settings panel)
  "admin.account.title": "Konto & Profil",
  "admin.account.subtitle":
    "Ihr Name, Ihre Dokumente, Sprache und Passwort. Rollen & Zugriff regelt, wer worauf zugreifen darf.",
  "admin.userMenu.accountProfile": "Konto & Profil",

  // The tab holds both routes to a new password (change it / reset by email),
  // so it is named for the subject rather than for one of the two actions.
  "owner.settings.tabPasswordArea": "Passwort",

  // Billing address (Personal information tab)
  "settings.billing.title": "Rechnungsadresse",
  "settings.billing.descLessor":
    "Erscheint auf den Verwaltungsrechnungen, die coliviq Ihnen stellt.",
  "settings.billing.street": "Straße und Hausnummer",
  "settings.billing.streetPlaceholder": "z. B. Musterstraße 12",
  "settings.billing.cityPostal": "PLZ und Ort",
  "settings.billing.cityPostalPlaceholder": "z. B. 60311 Frankfurt am Main",
  "settings.billing.lessorHint": "Erscheint auf Rechnungen an",
  "settings.billing.ambiguous":
    "Ihr Konto ist mit mehreren Eigentümerdatensätzen verknüpft — die Rechnungsadresse muss daher von coliviq geändert werden. Melden Sie sich bei uns, wir aktualisieren sie.",
  "settings.billing.noRecord":
    "Ihr Eigentümerdatensatz ist noch nicht angelegt, es gibt also keine Rechnungen zu adressieren. Sobald coliviq ihn verknüpft, erscheint die Adresse hier.",
  "settings.billing.saved": "Rechnungsadresse gespeichert.",
  "settings.billing.saveFailed":
    "Rechnungsadresse konnte nicht gespeichert werden.",
  "settings.saving": "Speichern …",

  // Nav (owner strip)
  "admin.nav.ownerAnalytics": "Analyse & Berichte",
  "admin.nav.myProperties": "Meine Objekte",
  "admin.nav.tenantDashboard": "Mieter-Dashboard",
  "admin.nav.properties": "Immobilien",

  // Property detail tabs / sections
  "admin.section.rooms": "Zimmer",
  "admin.tab.reminders": "Erinnerungen",
  "admin.tab.docs": "Dokumente",
  "admin.tab.bank": "Bank",
  "admin.tab.revenue": "Umsatz",

  // Breadcrumbs & actions
  "owner.portal.breadcrumbRoot": "Admin",
  "owner.portal.propertiesList": "Immobilien",
  "owner.property.aboutTitle": "Über diese Immobilie",
  "admin.action.edit": "Bearbeiten",
  "admin.action.uploadPhotos": "Fotos hochladen",
  "owner.property.backToList": "Zurück zu Immobilien",
  "owner.property.notFoundHelp":
    "Diese Immobilie wurde möglicherweise gelöscht oder Sie haben keinen Zugriff.",
  "admin.detail.more": "Mehr",
  "admin.detail.unhide": "Einblenden",
  "admin.detail.hideListing": "Aus Listing ausblenden",
  "admin.detail.delete": "Löschen …",

  // Contract tab copy (hardcoded EN subtitles)
  "admin.contract.lessorSubtitle":
    "Immobilieneigentümer — liefert Standard-Stellvertreter und Bankdaten.",
  "admin.contract.masterLeaseSubtitle":
    "Vertrag von coliviq mit dem Eigentümer.",
  "admin.contract.representorSubtitle":
    "Person, die für den Vermieter in Verträgen auftritt.",
  "admin.contract.noRepresentor":
    "Kein Stellvertreter festgelegt. Fügen Sie einen beim verknüpften Vermieter hinzu oder setzen Sie eine Immobilien-Überschreibung.",
  "admin.action.manageLessors": "Vermieter verwalten",

  "admin.section.masterLease": "Hauptmietvertrag",
  "admin.section.representor": "Standard-Stellvertreter",
  "admin.field.lessor": "Vermieter",
  "admin.field.masterRent": "Hauptmiete",
  "admin.field.leaseStart": "Mietbeginn",
  "admin.field.leaseEnd": "Mietende",
  "admin.field.noticePeriod": "Kündigungsfrist",
  "admin.section.fullRent": "Vollmiete",

  // Sidebar explanations
  "admin.sidebar.masterRentExplained":
    "coliviq verdient die Differenz zwischen eingezogener Mieter-Miete und der Hauptmiete.",
  "admin.sidebar.keynestHint":
    "KeyNest-Codes werden pro Zimmer gespeichert. Öffnen Sie ein Zimmer, um sie anzuzeigen oder zu bearbeiten.",

  // Admin reminders / Payments view (owner + operator console)
  "owner.reminders.pageTitle": "Zahlungen & Mahnungen",
  "owner.reminders.pageSubtitle":
    "Manuelle Eskalation. Das System berechnet die Stufe; Sie entwerfen und senden.",
  "owner.reminders.searchPlaceholder": "Mieter, Zimmer, Buchungen suchen …",
  "owner.reminders.searchPlaceholderEmbedded":
    "Suche nach Mieter, E-Mail, Zimmer oder Buchungs-ID …",
  "owner.reminders.export": "Exportieren",
  "owner.reminders.exportTitle": "Lädt Excel und PDF herunter",
  "owner.reminders.statTenants": "Mieter erfasst",
  "owner.reminders.statAcrossThisProperty": "In dieser Immobilie",
  "owner.reminders.statAcrossPrefix": "Über",
  "owner.reminders.propertySingular": "Immobilie",
  "owner.reminders.propertyPlural": "Immobilien",
  "owner.reminders.statOnTime": "Pünktlich",
  "owner.reminders.statOnTimeHint": "Für den aktuellen Zeitraum bezahlt",
  "owner.reminders.statOverdue": "Überfällig",
  "owner.reminders.tenantsWord": "Mieter",
  "owner.reminders.inFeesPhrase": "an Gebühren",
  "owner.reminders.filterAllTenants": "Alle",
  "owner.reminders.filterStage23": "2.–3. Erinnerung",
  "owner.reminders.filterInkasso": "Letzte Mahnung",
  "owner.reminders.stage1": "1. Erinnerung",
  "owner.reminders.stage2": "2. Erinnerung",
  "owner.reminders.stage3": "3. Erinnerung",
  "owner.reminders.statFiltered": "Gefilterte Zeilen",
  "owner.reminders.statFilteredHint":
    "Export verwendet aktuelle Filter und Suche",
  "owner.reminders.loadError": "Mahnungen konnten nicht geladen werden",
  "owner.reminders.retry": "Erneut versuchen",
  "owner.reminders.filterAllOverdue": "Überfällig",
  "owner.reminders.filterPaidMonth": "Diesen Monat bezahlt",
  "owner.reminders.sort.daysOverdue": "Nach überfälligen Tagen",
  "owner.reminders.sort.amount": "Nach Betrag",
  "owner.reminders.sort.name": "Nach Mietername",
  "owner.reminders.sortOpt.daysOverdue": "Überfällige Tage",
  "owner.reminders.sortOpt.amount": "Offener Betrag",
  "owner.reminders.sortOpt.name": "Mietername",
  "owner.reminders.colTenant": "Mieter",
  "owner.reminders.colRoomProperty": "Zimmer · Immobilie",
  "owner.reminders.colStatus": "Status",
  "owner.reminders.colDue": "Fällig",
  "owner.reminders.colOwed": "Offen",
  "owner.reminders.loading": "Wird geladen …",
  "owner.reminders.emptyFiltered": "Keine Buchungen passen zu den Filtern",
  "owner.reminders.emptyNoBookings": "Noch keine Buchungen zum Verfolgen",
  "owner.reminders.emptyFilteredHint":
    "Suchfilter löschen oder anderen Filter wählen.",
  "owner.reminders.emptyNoBookingsHint":
    "Buchungen erscheinen hier, sobald Mieter hinzugefügt wurden.",
  "owner.reminders.clearAria": "Leeren",

  // "My properties" grid — the not-yet-onboarded card and its explanation.
  "owner.card.yetToBeOnboarded": "Onboarding ausstehend",
  "owner.properties.notOnboardedCount": "Onboarding ausstehend",
  "owner.card.pendingInfoTitle": "Diese Immobilie ist noch nicht eingebunden",
  "owner.card.pendingInfoDesc":
    "coliviq richtet diese Immobilie gerade ein. Falls wir Ihnen einen Verwaltungsvertrag geschickt haben, finden Sie den Signaturlink in Ihrer E-Mail — andernfalls melden Sie sich bei uns, wir schließen das Onboarding ab.",

  // Settings → Notifications tab (owner-lifecycle email preferences)
  "owner.settings.tabNotifications": "Benachrichtigungen",
  "owner.notifications.emailTitle": "E-Mail-Benachrichtigungen",
  "owner.notifications.emailDesc":
    "Gesendet an {email}. Nur Aktivität in Ihren eigenen Immobilien.",
  "owner.notifications.allToggleTitle": "Alle E-Mail-Benachrichtigungen",
  "owner.notifications.allToggleSub":
    "Schalten Sie alle E-Mails unten gemeinsam ein oder aus.",
  "owner.notifications.groupOnboarding": "Onboarding",
  "owner.notifications.groupOnboardingSub":
    "Wenn Ihr Portal erstmals eingerichtet wird",
  "owner.notifications.itemPortalReady": "Eigentümer-Portal bereit",
  "owner.notifications.itemPortalReadySub":
    "Onboarding einer neuen Immobilie abgeschlossen",
  "owner.notifications.groupMoveIn": "Mietereinzug",
  "owner.notifications.groupMoveInSub":
    "Vertrag, Kaution, Schlüssel und Protokoll auf dem Weg zum Einzug",
  "owner.notifications.itemLeaseSigned": "Mietvertrag unterschrieben",
  "owner.notifications.itemLeaseSignedSub":
    "Ein Mietvertrag für Ihre Immobilie wurde unterschrieben",
  "owner.notifications.itemDepositReceived": "Kaution eingegangen",
  "owner.notifications.itemDepositReceivedSub":
    "Kautionszahlung eingegangen und bestätigt",
  "owner.notifications.itemKeysHandedOver": "Schlüsselübergabe erfolgt",
  "owner.notifications.itemKeysHandedOverSub":
    "Schlüssel als an den Mieter übergeben markiert",
  "owner.notifications.itemMoveInProtocol": "Einzugsprotokoll verfügbar",
  "owner.notifications.itemMoveInProtocolSub":
    "Ein Einzugsprotokoll wurde eingereicht",
  "owner.notifications.groupRent": "Mietzahlungen",
  "owner.notifications.groupRentSub":
    "Wenn die monatliche Miete eines Mieters eingeht",
  "owner.notifications.itemRentReceived": "Mietzahlung erhalten",
  "owner.notifications.itemRentReceivedSub":
    "Die monatliche Mietzahlung eines Mieters wurde erhalten",
  "owner.notifications.groupMoveOut": "Mieterauszug",
  "owner.notifications.groupMoveOutSub":
    "Bestätigung, Protokoll und Schlüssel auf dem Weg zum Auszug",
  "owner.notifications.itemMoveOutDate": "Auszugstermin bestätigt",
  "owner.notifications.itemMoveOutDateSub":
    "Der Auszugstermin des Mieters wurde bestätigt",
  "owner.notifications.itemMoveOutProtocol": "Auszugsprotokoll verfügbar",
  "owner.notifications.itemMoveOutProtocolSub":
    "Ein Auszugsprotokoll wurde eingereicht",
  "owner.notifications.itemKeysReturned": "Schlüssel zurückgegeben",
  "owner.notifications.itemKeysReturnedSub":
    "Die Schlüssel wurden vom Mieter zurückgegeben",

  // Owner Notifications page
  "owner.notifications.title": "Benachrichtigungen",
  "owner.notifications.managePreferences": "E-Mail-Einstellungen verwalten",
  "owner.notifications.markAllRead": "Alle als gelesen markieren",
  "owner.notifications.subtitle":
    "Jede E-Mail, die coliviq Ihnen zu Ihren Immobilien geschickt hat, an einem Ort.",
  "owner.notifications.allProperties": "Alle Immobilien",
  "owner.notifications.unread": "ungelesen",
  "owner.notifications.emptyTitle": "Noch keine Benachrichtigungen",
  "owner.notifications.emptyDesc":
    "Sobald sich etwas bei Ihren Immobilien tut, erscheint hier eine Aktualisierung.",
  "owner.notifications.markRead": "Als gelesen markieren",
  "owner.notifications.justNow": "Gerade eben",
  "owner.notifications.hoursAgoSuffix": " Std.",
  "owner.notifications.yesterday": "Gestern",
  "owner.notifications.daysAgoSuffix": " Tage",
  "owner.notifications.errorTitle":
    "Benachrichtigungen konnten nicht geladen werden",
  "owner.notifications.retry": "Erneut versuchen",

  // Tenant profile → Activity timeline: titles for a diff that touched several
  // booking columns (COL-542). DB rows win if a translator overrides them.
  "admin.tenantTabs.timeline.updatedPrefix": "Aktualisiert",
  "admin.tenantTabs.timeline.auditRentDueDay": "Fälligkeitstag der Miete",
  // Field labels for single-column titles ("Aktualisiert Monatliche Miete").
  // These mirror the translations rows seeded by 20260910181000, which take
  // priority — this map only covers a missing row or a stale cache.
  "admin.tenantTabs.timeline.auditFullName": "Name",
  "admin.tenantTabs.timeline.auditMonthlyRent": "Monatliche Miete",
  "admin.tenantTabs.timeline.auditDepositAmount": "Kaution",
  "admin.tenantTabs.timeline.auditMoveIn": "Einzug",
  "admin.tenantTabs.timeline.auditMoveOut": "Auszug",
  "admin.tenantTabs.timeline.auditActualMoveIn": "Tatsächlicher Einzug",
  "admin.tenantTabs.timeline.auditActualMoveOut": "Tatsächlicher Auszug",
  "admin.tenantTabs.contractStatusLabel": "Vertragsstatus",
  "admin.tenantTabs.timeline.auditPaymentStatus": "Zahlungsstatus",
  "admin.tenantTabs.timeline.auditRentReminders": "Mieterinnerungen",
  "admin.tenantTabs.timeline.amount": "Betrag",
  "admin.tenantTabs.timeline.auditDueDate": "Fälligkeitsdatum",
  "admin.tenantTabs.timeline.status": "Status",
  "admin.tenantTabs.timeline.auditFirstMonthRent": "Miete für den ersten Monat",
  "admin.tenantTabs.timeline.note": "Notiz",
  "admin.tenantTabs.timeline.auditFileName": "Dateiname",
  "admin.tenantTabs.timeline.auditLabel": "Bezeichnung",
  "admin.tenantTabs.timeline.change.generic": "Buchungsdetails aktualisiert",
  "admin.tenantTabs.timeline.change.two": "{a} und {b} aktualisiert",
  "admin.tenantTabs.timeline.change.many": "{a}, {b} und mehr aktualisiert",
  "admin.tenantTabs.timeline.change.rent.label": "Miete",
  "admin.tenantTabs.timeline.change.rent.single": "Miete aktualisiert",
  "admin.tenantTabs.timeline.change.deposit.label": "Kaution",
  "admin.tenantTabs.timeline.change.deposit.single": "Kaution aktualisiert",
  "admin.tenantTabs.timeline.change.contract.label": "Vertrag",
  "admin.tenantTabs.timeline.change.contract.single": "Vertrag aktualisiert",
  "admin.tenantTabs.timeline.change.payment.label": "Zahlung",
  "admin.tenantTabs.timeline.change.payment.single":
    "Zahlungsdaten aktualisiert",
  "admin.tenantTabs.timeline.change.reminders.label": "Erinnerungen",
  "admin.tenantTabs.timeline.change.reminders.single":
    "Erinnerungsstatus aktualisiert",
  "admin.tenantTabs.timeline.change.moveIn.label": "Einzug",
  "admin.tenantTabs.timeline.change.moveIn.single": "Einzugsdaten aktualisiert",
  "admin.tenantTabs.timeline.change.moveOut.label": "Auszug",
  "admin.tenantTabs.timeline.change.moveOut.single":
    "Auszugsdaten aktualisiert",
  "admin.tenantTabs.timeline.change.room.label": "Zimmer",
  "admin.tenantTabs.timeline.change.room.single":
    "Zimmerzuordnung aktualisiert",
  "admin.tenantTabs.timeline.change.key.label": "Schlüssel",
  "admin.tenantTabs.timeline.change.key.single": "Schlüsseldaten aktualisiert",
  "admin.tenantTabs.timeline.change.status.label": "Status",
  "admin.tenantTabs.timeline.change.status.single":
    "Buchungsstatus aktualisiert",
  "admin.tenantTabs.timeline.change.archive.label": "Archivierung",
  "admin.tenantTabs.timeline.change.archive.single":
    "Archivstatus aktualisiert",
  "admin.tenantTabs.timeline.change.contact.label": "Kontaktdaten",
  "admin.tenantTabs.timeline.change.contact.single":
    "Kontaktdaten aktualisiert",
  "admin.tenantTabs.timeline.change.name.label": "Name",
  "admin.tenantTabs.timeline.change.name.single": "Name aktualisiert",
  "admin.tenantTabs.timeline.change.notes.label": "Notizen",
  "admin.tenantTabs.timeline.change.notes.single": "Notizen aktualisiert",

  // Property wizard V3 — step-3 checklist review + gating (COL-529)
  "admin.checklist.introOwner":
    "Jede Wohnung braucht alle 15 Nachweise, bevor Sie absenden können. Füllen Sie jeden Punkt für jede Wohnung aus, um fortzufahren.",
  "admin.checklist.ownerGateIncomplete":
    "Jede Wohnung muss alle 15 Checklisten-Nachweise erfüllen, bevor Sie fortfahren können.",
  "admin.checklist.ownerGateSmoke":
    'Jede Wohnung braucht einen Rauchmelder — klären Sie die Antwort „Nein", bevor Sie fortfahren.',
  "admin.checklist.adminSmokeWarning":
    'Bei mindestens einer Wohnung ist die Rauchmelder-Prüfung mit „Nein" markiert — die Freischaltung ist blockiert, bis das geklärt ist.',
  "admin.wizard.step3.checklistLabel": "Checkliste",
  "admin.wizard.step3.checklistDocs": "Dokumente",
  "admin.wizard.step3.checklistUnit": "Wohnung",
  "admin.wizard.step3.checklistContacts": "Kontakte",
  "admin.wizard.step3.checklistBlocked":
    "Kein Rauchmelder — Freischaltung blockiert",
  "admin.checklist.ctl.photoHint":
    "Alle 4 Eckfotos für jedes Schlafzimmer und den Gemeinschaftsbereich sowie 2 Außenaufnahmen des Gebäudes.",
  "admin.checklist.introOwnerGrandfathered":
    'Sie können erneut absenden, ohne jeden Nachweis auszufüllen. Erledigen Sie die übrigen Punkte, wann immer Sie möchten — nur ein ausdrückliches „Nein" beim Rauchmelder blockiert Sie.',
  "admin.checklist.noApartments":
    "Fügen Sie zuerst eine Wohnung hinzu, um ihre Checkliste zu verfolgen.",
  "admin.checklist.apartmentLabel": "Wohnung",
  "admin.properties.discardHintDraft":
    "Sie können Ihren Fortschritt als Entwurf speichern und später fortfahren oder alle eingegebenen Daten verwerfen.",
  "admin.v2.propertyNew.submitErrorChecklistIncomplete":
    "Die Go-live-Checkliste ist noch nicht vollständig — jede Wohnung braucht alle 15 Punkte erfüllt (Rauchmelder bestätigt), bevor Sie absenden können.",
  "admin.v2.propertyNew.submitErrorNoApartments":
    "Fügen Sie mindestens eine Wohnung hinzu, bevor Sie absenden.",
};
